import { useParams } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import z from "zod";

import { useTheme } from "@mui/material/styles";
import Chip from "@mui/material/Chip";

import core from "core";
import numbers from "core/numbers";
import strings from "core/strings";
import useFilters from "core/hooks/useFilters";
import useNav from "core/hooks/useNav";

import Icon from "ui/atom/Icon";
import Button from "ui/base/Button";
import { ApplicationStatus, type PartnerInfoApi } from "api/models/partner";

import type { FormData } from "features/form/models/types";
import { useForm } from "features/form/fabric";

import { useMutateApplication, useMutatePartner, useQueryApplication, useQueryCms, useQueryCurrencies, useQueryPartner, useQueryPartners, useQueryPaywall } from "./data";
import { AplicationStatusColor } from "./models";


const tab = {
	other: [ApplicationStatus.UnderReview, ApplicationStatus.Rejected, ApplicationStatus.Inactive],
	active: [ApplicationStatus.Approved]
};

export const usePartners = () => {
	const { filters, setFilters } = useFilters("/partners/");
	const status = filters.status ? tab[filters.status] : undefined;
	const query = useQueryPartners(status, filters.page);
	const columnHelper = createColumnHelper<PartnerInfoApi>();
	const theme = useTheme();
	const columns = [
		columnHelper.accessor("id", {
			header: "Id",
			cell: info => info.renderValue(),
			size: 30
		}),
		columnHelper.accessor("name", {
			header: "Name",
			cell: info => info.getValue()
		}),
		columnHelper.accessor("url", {
			header: "Url",
			cell: info => info.renderValue() && strings.truncate(info.renderValue()!, 50)
		}),
		columnHelper.display({
			header: "Verified",
			cell: ({ row }) => {
				const verified = row.original.emailVerified;
				return <Icon name={verified ? "badge-check" : "badge"} color={verified ? theme.palette.success.main : theme.palette.text.disabled} />;
			}
		}),
		columnHelper.display({
			header: "Status",
			cell: ({ row }) => {
				const status = row.original.status;
				return <Chip label={strings.pascal.addSpaces(status)} color={AplicationStatusColor[status]} />;
			}
		}),
		columnHelper.display({
			id: "button",
			header: "Actions",
			cell: ({ row }) => <Button.Icon icon="chevron-right" to={(!status || status === tab.other) ? "/partners/application/$id" : "/partners/$id"} params={{ id: row.original.id?.toString() }} color="secondary" title="Details" />
		})
	];

	return { query, columns, filters, tab, setFilters };
};

const PartnerSchema = z.object({
	partner: z.object({
		name: z.string().min(1, "Name is required"),
		url: z.url("Invalid URL"),
		status: z.union([z.enum(ApplicationStatus), z.literal("")]).optional(),
		logo: z.object({
			light: z.union([z.string(), z.instanceof(File)]).optional(),
			dark: z.union([z.string(), z.instanceof(File)]).optional(),
			logomark: z.union([z.string(), z.instanceof(File)]).optional()
		}).optional(),
		cmsId: z.string().optional(),
		paywallId: z.string().optional(),
		payoutCurrencyId: z.string().optional(),
		ein: z.string().optional()
	}),
	contact: z.object({
		name: z.string().min(1, "Name is required"),
		title: z.string().min(1, "Title is required"),
		email: z.email("Invalid email address"),
		phone: z.string().optional().nullable(),
		company: z.string().min(1, "Company is required"),
		emailVerified: z.boolean()
	})
});

export const usePartner = () => {
	const params = useParams({ from: "/partners/$id" });
	const query = useQueryPartner(params.id);
	const mutation = useMutatePartner();
	const cms = useQueryCms();
	const currencies = useQueryCurrencies();
	const nav = useNav();

	const defaultValues: FormData<typeof PartnerSchema> = {
		partner: {
			name: query.data?.partner.name || "",
			url: query.data?.partner.url || "",
			status: query.data?.partner.status || "",
			logo: {
				light: query.data?.partner.logo?.light || "",
				dark: query.data?.partner.logo?.dark || "",
				logomark: query.data?.partner.logo?.logoMark || ""
			},
			cmsId: query.data?.partner.cmsId?.toString() || "",
			paywallId: query.data?.partner.paywallId?.toString() || "",
			payoutCurrencyId: query.data?.partner.payoutCurrencyId?.toString() || "",
			ein: query.data?.partner.ein || ""
		},
		contact: {
			name: query.data?.contact.name || "",
			title: query.data?.contact.title || "",
			email: query.data?.contact.email || "",
			phone: numbers.toPhone(query.data?.contact.phone?.slice(2)) || "",
			company: query.data?.contact.company || "",
			emailVerified: query.data?.contact.emailVerified || false
		}
	};

	const form = useForm({
		defaultValues,
		validators: {
			onSubmit: PartnerSchema
		},
		canSubmitWhenInvalid: true,
		onSubmit: async ({ value }) => {
			const logo = value.partner.logo;
			const light = (logo?.light && core.is.file(logo?.light)) ? await core.toBase64(logo.light) : logo?.light;
			const dark = (logo?.dark && core.is.file(logo?.dark)) ? await core.toBase64(logo.dark) : logo?.dark;
			const logomark = (logo?.logomark && core.is.file(logo?.logomark)) ? await core.toBase64(logo.logomark) : logo?.logomark;

			await mutation.mutateAsync({
				id: params.id,
				body: {
					partner: {
						name: value.partner.name.trim(),
						url: strings.trimTrailingSlash(value.partner.url.trim()),
						status: value.partner.status || undefined,
						logo: {
							light: light as string,
							dark: dark as string,
							logoMark: logomark as string
						},
						cmsId: value.partner.cmsId ? parseInt(value.partner.cmsId) : undefined,
						paywallId: value.partner.paywallId ? parseInt(value.partner.paywallId) : undefined,
						payoutCurrencyId: value.partner.payoutCurrencyId ? parseInt(value.partner.payoutCurrencyId) : undefined,
						ein: value.partner.ein?.trim() || undefined
					},
					contact: {
						name: value.contact.name.trim(),
						title: value.contact.title.trim(),
						email: value.contact.email.trim(),
						phone: value.contact.phone ? strings.sanitizePhoneNumber("1" + value.contact.phone.trim()) : null,
						company: value.contact.company.trim(),
						emailVerified: value.contact.emailVerified
					}
				}
			});
		}
	});

	const back = () => nav.go({ to: "/partners", search: { status: "active" } });

	return { ...query, back, cms, currencies, form, id: params.id, mutation };
};

const ApplicationSchema = z.object({
	contact: z.object({
		name: z.string().min(1, "Name is required"),
		title: z.string().min(1, "Title is required"),
		email: z.email("Invalid email address"),
		phone: z.string().optional().nullable(),
		company: z.string().min(1, "Company is required")
	}),
	partner: z.object({
		name: z.string().min(1, "Publication name is required"),
		url: z.url("Invalid URL")
	})
});

export const useApplication = () => {
	const params = useParams({ from: "/partners/application/$id" });
	const partner = useQueryApplication(params.id);
	const mutation = useMutateApplication();
	const nav = useNav();

	const defaultValues: FormData<typeof ApplicationSchema> = {
		contact: {
			name: partner.data?.contact.name || "",
			title: partner.data?.contact.title || "",
			email: partner.data?.contact.email || "",
			phone: numbers.toPhone(partner.data?.contact.phone?.slice(2)) || "",
			company: partner.data?.contact.company || ""
		},
		partner: {
			name: partner.data?.partner.name || "",
			url: partner.data?.partner.url || ""
		}
	};

	const form = useForm({
		defaultValues,
		validators: {
			onSubmit: ApplicationSchema
		},
		canSubmitWhenInvalid: true,
		onSubmitMeta: { type: "save" as "save" | "approve" | "reject" },
		onSubmit: async ({ value, meta }) => {
			await mutation.mutateAsync({
				id: params.id,
				body: {
					contact: {
						name: value.contact.name.trim(),
						title: value.contact.title.trim(),
						email: value.contact.email.trim(),
						phone: value.contact.phone ? strings.sanitizePhoneNumber("1" + value.contact.phone.trim()) : null, /* todo: improve country code */
						company: value.contact.company.trim(),
						emailVerified: partner.data?.contact.emailVerified || false
					},
					partner: {
						name: value.partner.name.trim(),
						url: strings.trimTrailingSlash(value.partner.url.trim())
					}
				},
				type: meta.type
			});
		}
	});

	const back = () => nav.go({ to: "/partners" });

	return { ...partner, back, form, mutation, id: params.id };
};

export const usePaywall = useQueryPaywall;